import PropTypes from 'prop-types';
import React from 'react';
import {
  defineMessages,
  injectIntl,
  intlShape,
  FormattedMessage,
} from 'react-intl';

import Button from 'brastrap/common/button/Button';
import FieldInput from 'brastrap/common/field-input/FieldInput';
import FieldSet from 'brastrap/common/fieldset/Fieldset';
import Message from 'brastrap/common/message/Message';
import RequestMagazineFieldSet from 'brastrap/common/request-magazine-fieldset/RequestMagazineFieldset';
import AddressFieldSet from 'brastrap/common/address-field-set/AddressFieldSet';

const messages = defineMessages({
  confirmationTitle: {
    id: 'title.confirmation',
    defaultMessage: 'Confirmation',
  },
  email: { id: 'request-magazine.email', defaultMessage: 'Email' },
  errorTitle: { id: 'title.error', defaultMessage: 'Error' },
  requestMagazine: {
    id: 'button.caption.request-magazine',
    defaultMessage: 'Submit',
  },
  saving: { id: 'button.caption.saving', defaultMessage: 'Saving…' },
});

const RequestMagazineForm = props => {
  const {
    countries,
    fields,
    isSaving,
    handleSubmit,
    intl: { formatMessage },
    namespace,
    values: {
      address: { countryId },
    },
  } = props;

  const onSubmit = (values, dispatch) => {
    props.onSubmitPayload(values, dispatch);
  };

  const confirmationMessage = !props.confirmationMessage ? null : (
    <Message
      id="confirmationMessage"
      title={formatMessage(messages.confirmationTitle)}
      modifiers={['confirm', 'banner']}
      onClose={props.onCloseMessages}
      dismissable
    >
      <FormattedMessage
        id="form-submission.confirmation"
        defaultMessage="Your request has been accepted."
      />
    </Message>
  );

  const errorMessage = !props.errorMessage ? null : (
    <Message
      id="errorMessage"
      title={formatMessage(messages.errorTitle)}
      modifiers={['warn', 'banner']}
      onClose={props.onCloseMessages}
      dismissable
    >
      <FormattedMessage
        id="form-submission.error"
        defaultMessage="There was a problem submitting your request. Please try again later."
      />
    </Message>
  );

  const emailFieldSet = (
    <FieldSet title="Your Email Address (optional)">
      <p style={{ margin: 7, fontSize: '1.1em' }}>
        <FormattedMessage
          id="request-magazine.email-text"
          defaultMessage="If you'd also like to receive emails about new collections, seasonal sales
                          and latest season's must-haves, simply provide your email address:"
        />
      </p>
      <FieldInput
        {...fields.email}
        label={formatMessage(messages.email)}
        modifiers={['large', 'inline']}
        floatLabel
        id="request-magazine-email"
        note={fields.email.error}
      />
    </FieldSet>
  );

  /* We place the confirmation and error messages at the bottom in case the user has to scroll to see all the form,
   * in which case they're unlikely to scroll to the top to see the messages.
   */
  return (
    <div>
      <form className="c-form request-magazine">
        <RequestMagazineFieldSet {...fields.details} />
        <AddressFieldSet
          countries={countries}
          namespace={namespace}
          {...fields.address}
          selectedCountryId={countryId}
        />

        <br />

        {emailFieldSet}

        <div className="c-form__actions">
          <Button
            id="request-magazine-submit"
            label={formatMessage(messages.requestMagazine)}
            disabled={isSaving}
            onClick={handleSubmit(onSubmit)}
            modifiers={['large', 'requestMagazine']}
          />
          {/* We could use an <Activity /> here, but the way it is laid out is wrong */}
          {isSaving && (
            <span style={{ margin: 10 }}>{formatMessage(messages.saving)}</span>
          )}
        </div>
      </form>
      {confirmationMessage}
      {errorMessage}
    </div>
  );
};

RequestMagazineForm.propTypes = {
  countries: PropTypes.array,
  onSubmitPayload: PropTypes.func.isRequired,
  fields: PropTypes.shape({
    details: PropTypes.object.isRequired,
    address: PropTypes.object.isRequired,
    email: PropTypes.object.isRequired,
  }),
  namespace: PropTypes.string,
  values: PropTypes.shape({
    address: PropTypes.shape({
      countryId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
  }),
  isSaving: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  confirmationMessage: PropTypes.string,
  errorMessage: PropTypes.string,
  onCloseMessages: PropTypes.func,
  intl: intlShape,
};

RequestMagazineForm.defaultProps = {
  countries: [],
  values: {
    address: {},
  },
};

export default injectIntl(RequestMagazineForm);
